import React from "react";

// Content for /product-release/rev34
//
// This is a static page — just write the release content directly as JSX below.
// Reuse the "product-release__*" classes (see ../product-release.css) to keep
// consistent spacing/typography, or add your own markup as needed.

const Rev34 = () => {
	return (
		<>
			<header className="product-release__header">
				<img className="product-release__logo" src="/assets/srj-black-logo.svg" alt="SRJ" />
				{/* TODO: Add release date, e.g. <span className="product-release__date">August 25, 2026</span> */}
				<h1 className="product-release__title">🚀 What's new in Workflow (rev34)</h1>
				<p className="product-release__summary">{/* 1-2 sentence overview of this release. */}</p>
			</header>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🧮 Automated Bookkeeping Assistant</h2>
				<p className="product-release__section-body">Bookkeeping Tool - Built for the year-end compilation</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<h3 className="product-release__section-subheading">⚙️ How it works</h3>
				<ol className="product-release__ordered-list">
					<li>
						Import. Upload the client's bank and credit card statements along with their GL export. Every statement is checked before anything is
						saved — the amounts are re-derived from the bank's own running balance, and the opening and closing balances are tied out against what
						the statement says. If a file doesn't add up, you're told why up front instead of finding out three steps later.
					</li>
					<li>
						Classify. Transactions are matched against the client's own general ledger first, since that's the record of what was actually posted.
						Anything the ledger doesn't cover goes to the AI. Whatever's left comes to you.
					</li>
					<li>
						Review. Everything is grouped so you can work through it: transfers, items needing sign-off, unclassified, and approved. The tool flags
						accounts with no financial-statement mapping, and descriptions it has answered two different ways, so inconsistencies surface rather
						than sitting in the file.
					</li>
					<li>Download. The workbook comes out with prior-year comparatives and a tab per bank account showing how each line was booked.</li>
				</ol>

				<h3 className="product-release__section-subheading">💡 Worth knowing</h3>
				<ul className="product-release__bullet-list">
					<li>
						Transfers between a client's own accounts are matched as pairs, so a payment that appears on two statements is counted once rather than
						inflating both sides.
					</li>
					<li>
						Sales tax rates come from the client's own general ledger — what they actually posted to each account, rather than a general assumption.
						A client who records no tax is treated as exempt.
					</li>
					<li>The province is read from the statements rather than asked for, and you can override it. Your choice is never overwritten.</li>
					<li>Prior year carries forward — a finished file's closing balances become next year's openings and comparatives.</li>
					<li>
						Nothing is destroyed. Removing a transaction from the books archives it with a record of who removed it and why, and it can be restored.
					</li>
				</ul>

				<div className="product-release__note">
					<span className="product-release__note-title">👀 A note on review</span>
					<p className="product-release__note-body">
						The tool does the first pass, not the final one. Every classification it suggests is visible, traceable to where it came from, and
						changeable — and the file still needs your review before the statements are signed. It's there to remove the repetitive coding, not the
						judgement.
					</p>
				</div>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">📊 Dynamic Trackers</h2>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<ul className="product-release__bullet-list">
					<li>
						One grid for all engagements — it pulls your engagements into a single table, grouped by client, so you see everything (status, dates,
						owners, custom fields) in one place instead of opening files one by one.
					</li>
					<li>
						Fully customizable view — you choose which columns to show or hide, drag them into any order, resize them, and click a header to sort;
						the tracker remembers all of it.
					</li>
					<li>
						Powerful filtering — a filter builder lets you combine rules with AND/OR logic (e.g. "Status is In Progress AND Year End is 2025") and
						apply them so only matching engagements load.
					</li>
					<li>
						Saved trackers you can share — your current filters, sort, and column layout can be saved as a named tracker view, kept private or
						shared company-wide so the whole team sees the same setup.
					</li>
					<li>
						Edits entries in place — it's not read-only: you can update engagement values directly in the grid (or through an edit form) and the
						changes save back to the engagement itself.
					</li>
					<li>
						Drill-down sub-grids — expanding an engagement row reveals nested Task and User grids (loaded on demand), so you can review and manage
						the work inside an engagement without leaving the tracker.
					</li>
					<li>
						Remembers where you were — expanded clients, filters, sort, and layout are persisted in your browser, so the tracker looks exactly how
						you left it next time you open it.
					</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">📇 Client Communication Logger</h2>
				<p className="product-release__section-body">
					Every client email, call, and meeting now lands in one organized place — instead of an inbox search and someone's memory of what was said.
				</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<h3 className="product-release__section-subheading">⚙️ How it works</h3>
				<ol className="product-release__ordered-list">
					<li>Capture. Client emails, call recordings, and meeting transcripts are pulled into a single communications log for that client.</li>
					<li>
						Summarize. AI turns each thread, call, or transcript into a short, reusable summary — the kind you'd actually forward to a colleague or
						paste into a file note.
					</li>
					<li>
						Organize. Every client gets their own communications folder in Google Drive, built to the same structure across clients, so anyone on
						the team can find a specific call or thread without asking around.
					</li>
					<li>
						Surface. Summaries of recent client calls show up right inside Workflow, next to the client, so you can catch up without leaving the
						tool.
					</li>
				</ol>

				<h3 className="product-release__section-subheading">💡 Worth knowing</h3>
				<ul className="product-release__bullet-list">
					<li>
						Google Meet calls are covered out of the box — Meet already generates a transcript automatically, so there's nothing extra to turn on.
					</li>
					<li>Zoom and Teams meetings are supported too, bringing in meeting notes and transcripts where they're available.</li>
					<li>
						Summaries are a starting point for your file notes, not a replacement for them — the source email, call, or transcript is always one
						click away.
					</li>
				</ul>
			</section>

			<section className="product-release__section product-release__changelog">
				<h2 className="product-release__section-heading">🎫 Full ticket list</h2>
				<ul className="product-release__changelog-list">
					<li>W-976 - Create view filters for Dynamic and regular fields</li>
					<li>W-924 - Fix QBO connection issue in workflow</li>
					<li>W-927 - Update QBO Access token logic and CRON job</li>
					<li>W-930 - Add Bank Reconciliation functionality</li>
					<li>W-918 - Nisha Zaidi - Creation of FAQ section in wf to upload training material</li>
					<li>W-920 - Develop AI based FAQ</li>
					<li>W-776 - Add Shareholder Loan as a default option on the dropdown to the Bookkeeping Tool</li>
					<li>W-940 - Add Loan Reconciliation functionality</li>
					<li>W-775 - Add the ability to filter just the 'Accounts' for easier allocations</li>
					<li>W-778 - Reverse signage of imported csv transactions (i.e. dr should be cr, etc.)</li>
					<li>W-772 - Add the ability to use the GL as a reference for the mapping of accounts for the Bookkeeping Tool</li>
					<li>W-777 - Mark where necessary accounts to be exclusive of tax where applicable for the Bookkeeping Tool</li>
					<li>W-770 - Add the ability to add an account that's not in CW for the Bookkeeping Tool</li>
					<li>W-951 - Dynamic Engagement forms</li>
					<li>W-934 - Sajid Shaikh - Bookkeeping Tools not working</li>
					<li>W-926 - Bookkeeping Tool Feedback</li>
					<li>W-988 - Nisha Zaidi - Restrictions on the access of GDrive integration (W-936)</li>
					<li>W-936 - GDrive integration - integrate gdrive in wf to link each client to corresponding gdrive folder</li>
					<li>W-943 - Client Communication Logger in WF</li>
					<li>W-941 - Add default views to tasks screen in WF</li>
					<li>W-937 - M Abubakar - Remove the current user as a member on engagement's tasks</li>
					<li>W-938 - Nisha Zaidi - Issue with engagement name</li>
					<li>W-913 - Nisha Zaidi - Search criteria improvement in WF</li>
					<li>W-912 - Nisha Zaidi - Adding option to select client status in filters</li>
					<li>W-919 - Nisha Zaidi - Wrong user appearing in tasks</li>
					<li>W-904 - Nisha Zaidi - Date is showing different in different time zones in Tasks tab</li>
					<li>W-894 - Nisha Zaidi - Conditional filtration for users</li>
					<li>W-907 - Nisha Zaidi - Marking an engagement as COMPLETED</li>
					<li>W-890 - Nisha Zaidi - Changes in WF view/interface</li>
					<li>W-878 - Nisha Zaidi - Notification for adding a note</li>
				</ul>
			</section>
		</>
	);
};

export default Rev34;
